import { Card, CardHeader, CardContent } from '../../../components/Card';
import { Button } from '../../../components/Button';
import { Badge } from '../../../components/Badge';
import { Plus, Zap, Settings, RefreshCw, Power } from 'lucide-react';

export function Automation() {
    return (
        <div className="space-y-6 flex flex-col h-[calc(100vh-8rem)]">
            <div className="flex justify-between items-center shrink-0">
                <div>
                    <h1 className="tracking-tight text-foreground font-display text-page-title">Automation</h1>
                    <p className="text-muted-foreground">Automate the busywork. Trigger actions on events.</p>
                </div>
                <Button variant="primary">
                    <Plus className="w-4 h-4 mr-2" />
                    New Rule
                </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 flex-1 min-h-0">
                <Card className="md:col-span-1 flex flex-col h-full bg-secondary/10">
                    <CardHeader>
                        <h3 className="text-foreground font-display text-card-title">Categories</h3>
                    </CardHeader>
                    <CardContent className="space-y-2 flex-1">
                        <Button variant="ghost" className="w-full justify-between font-medium bg-background border border-border">
                            All Rules <Badge variant="secondary">12</Badge>
                        </Button>
                        <Button variant="ghost" className="w-full justify-between text-muted-foreground">
                            Issue Tracking <Badge variant="secondary" className="bg-transparent text-muted-foreground">5</Badge>
                        </Button>
                        <Button variant="ghost" className="w-full justify-between text-muted-foreground">
                            Notifications <Badge variant="secondary" className="bg-transparent text-muted-foreground">4</Badge>
                        </Button>
                        <Button variant="ghost" className="w-full justify-between text-muted-foreground">
                            Time Tracking <Badge variant="secondary" className="bg-transparent text-muted-foreground">2</Badge>
                        </Button>
                        <Button variant="ghost" className="w-full justify-between text-muted-foreground">
                            Maintenance <Badge variant="secondary" className="bg-transparent text-muted-foreground">1</Badge>
                        </Button>
                    </CardContent>
                </Card>

                <div className="md:col-span-3 flex flex-col min-h-0 bg-card border border-border rounded-lg">
                    <div className="p-4 border-b border-border bg-secondary/5 shrink-0 flex justify-between items-center">
                        <h3 className="text-foreground font-display text-card-title">Active Automation Rules</h3>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                            <RefreshCw className="w-4 h-4" /> Last evaluated: Just now
                        </div>
                    </div>
                    <div className="p-4 flex-1 overflow-auto space-y-4 bg-background">
                        <Card className="border-l-4 border-l-success">
                            <CardContent className="p-4 flex items-center justify-between">
                                <div className="space-y-1">
                                    <div className="flex items-center gap-2">
                                        <Zap className="w-4 h-4 text-warning" />
                                        <h4 className="text-foreground text-sm font-semibold">Close Stale Issues</h4>
                                    </div>
                                    <p className="text-sm text-muted-foreground flex items-center gap-2">
                                        <span className="font-medium text-foreground text-xs bg-secondary px-1.5 py-0.5 rounded">WHEN</span> Time since last update &gt; 30 days
                                        <span className="font-medium text-foreground text-xs bg-secondary px-1.5 py-0.5 rounded">THEN</span> Transition to "Closed" & Add comment
                                    </p>
                                </div>
                                <div className="flex items-center gap-4 pl-4 border-l border-border">
                                    <div className="text-center">
                                        <p className="text-xs text-muted-foreground">Runs</p>
                                        <p className="font-medium text-foreground">1,204</p>
                                    </div>
                                    <Button variant="outline" size="sm" className="text-success border-success/30 hover:bg-success/90 hover:text-success-foreground">
                                        <Power className="w-4 h-4 mr-1" /> Active
                                    </Button>
                                    <Button variant="ghost" size="icon"><Settings className="w-4 h-4" /></Button>
                                </div>
                            </CardContent>
                        </Card>

                        <Card className="border-l-4 border-l-success">
                            <CardContent className="p-4 flex items-center justify-between">
                                <div className="space-y-1">
                                    <div className="flex items-center gap-2">
                                        <Zap className="w-4 h-4 text-warning" />
                                        <h4 className="text-foreground text-sm font-semibold">Assign PR Reviewers</h4>
                                    </div>
                                    <p className="text-sm text-muted-foreground flex items-center gap-2">
                                        <span className="font-medium text-foreground text-xs bg-secondary px-1.5 py-0.5 rounded">WHEN</span> Issue transitions to "In Review"
                                        <span className="font-medium text-foreground text-xs bg-secondary px-1.5 py-0.5 rounded">THEN</span> Assign round-robin to "Engineering"
                                    </p>
                                </div>
                                <div className="flex items-center gap-4 pl-4 border-l border-border">
                                    <div className="text-center">
                                        <p className="text-xs text-muted-foreground">Runs</p>
                                        <p className="font-medium text-foreground">843</p>
                                    </div>
                                    <Button variant="outline" size="sm" className="text-success border-success/30 hover:bg-success/90 hover:text-success-foreground">
                                        <Power className="w-4 h-4 mr-1" /> Active
                                    </Button>
                                    <Button variant="ghost" size="icon"><Settings className="w-4 h-4" /></Button>
                                </div>
                            </CardContent>
                        </Card>
                        
                        <Card className="border-l-4 border-l-muted-foreground opacity-75">
                            <CardContent className="p-4 flex items-center justify-between">
                                <div className="space-y-1">
                                    <div className="flex items-center gap-2">
                                        <Zap className="w-4 h-4 text-warning" />
                                        <h4 className="text-foreground text-sm font-semibold">Weekly Timesheet Reminder</h4>
                                    </div>
                                    <p className="text-sm text-muted-foreground flex items-center gap-2">
                                        <span className="font-medium text-foreground text-xs bg-secondary px-1.5 py-0.5 rounded">WHEN</span> Day is Friday AND Time is 16:00
                                        <span className="font-medium text-foreground text-xs bg-secondary px-1.5 py-0.5 rounded">THEN</span> Send Slack message to users with missing time
                                    </p>
                                </div>
                                <div className="flex items-center gap-4 pl-4 border-l border-border">
                                    <div className="text-center">
                                        <p className="text-xs text-muted-foreground">Runs</p>
                                        <p className="font-medium text-foreground">0</p>
                                    </div>
                                    <Button variant="outline" size="sm" className="text-muted-foreground hover:bg-secondary">
                                        <Power className="w-4 h-4 mr-1" /> Paused
                                    </Button>
                                    <Button variant="ghost" size="icon"><Settings className="w-4 h-4" /></Button>
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                </div>
            </div>
        </div>
    );
}
